import type { SaleLine } from './contract';

/** Montos de una venta que el portal lee como «vendido». */
export interface SoldAmountSource {
    soldCents?: number | null;
    equipmentPriceCents?: number | null;
    amountCents: number;
}

type NetLine = Pick<SaleLine, 'basePriceCents' | 'qty' | 'discountCents' | 'role'>;
type SkuLine = NetLine & Pick<SaleLine, 'sku' | 'components'>;

/** Lo vendido de la venta (DEC-46); sin `soldCents` del silo cae al precio del equipo y luego a lo cobrado. */
export function soldCentsOf(sale: SoldAmountSource): number {
    return sale.soldCents ?? sale.equipmentPriceCents ?? sale.amountCents;
}

/** Neto de la línea como el silo: base × piezas − descuento, nunca negativo; el regalo vale 0. */
export function lineNetCents(line: NetLine): number {
    if (line.role === 'GIFT') return 0;
    return Math.max(0, line.basePriceCents * line.qty - (line.discountCents ?? 0));
}

/** ¿La venta trae el SKU? Por el equipo, por `skus`, por una línea o por un kit que lo incluye. */
export function saleHasSku(
    sale: { sku?: string | null; skus?: string[]; lines?: Pick<SaleLine, 'sku' | 'components'>[] },
    sku: string,
): boolean {
    if (sale.sku === sku || sale.skus?.includes(sku)) return true;
    return (sale.lines ?? []).some((l) => l.sku === sku || l.components.some((c) => c.sku === sku));
}

/** Lo vendido de un SKU en la venta: sus líneas, o la del kit que lo trae; sin líneas, la venta entera. */
export function skuSoldCents(sale: SoldAmountSource & { lines?: SkuLine[] }, sku: string): number {
    const lines = sale.lines ?? [];
    const own = lines.filter((l) => l.sku === sku);
    if (own.length > 0) return own.reduce((total, l) => total + lineNetCents(l), 0);
    const kits = lines.filter((l) => l.components.some((c) => c.sku === sku));
    if (kits.length > 0) return kits.reduce((total, l) => total + lineNetCents(l), 0);
    return soldCentsOf(sale);
}
